import React, { useState } from "react";
import {
    Code2,
    Cpu,
    Database,
    Mail,
    Linkedin,
    ArrowRight,
    Calendar,
} from "lucide-react";
import { motion } from "framer-motion";
import Sidebar from "./components/Sidebar";
import SectionCard from "./components/SectionCard";
import TechGroup from "./components/TechGroup";
import TimelineItem from "./components/TimelineItem";
import ProjectCard from "./components/ProjectCard";
import type { Theme } from "./components/ThemeToggle";

const App: React.FC = () => {
    const [theme, setTheme] = useState<Theme>("light");

    return (
        <div className={theme === "dark" ? "dark" : ""}>
            <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-slate-50 dark:bg-gradient-to-br dark:from-slate-50 dark:via-slate-100 dark:to-slate-50 dark:text-slate-900">
                <div className="h-1 bg-gradient-to-r from-emerald-400 via-cyan-400 to-indigo-500" />

                <div className="mx-auto flex max-w-6xl flex-col gap-8 px-4 py-10 lg:flex-row lg:py-16">
                    {/* Sidebar */}
                    <Sidebar theme={theme} setTheme={setTheme} />

                    {/* Main content */}
                    <main className="space-y-8 lg:w-2/3">
                        {/* About */}
                        <SectionCard id="about" title="About">
                            <p className="text-sm text-slate-300 dark:text-slate-700">
                                I’m a backend-focused engineer with extensive experience
                                designing and operating scalable systems. I enjoy building
                                robust APIs, email processing pipelines, and automation tooling
                                — with a strong emphasis on clarity, observability, and
                                long-term maintainability.
                            </p>
                            <p className="mt-4 text-sm text-slate-300 dark:text-slate-700">
                                I’ve worked across startups and established products, often
                                owning the full lifecycle: architecture, implementation, CI/CD,
                                deployment, and incident response. I’m comfortable diving deep
                                into performance diagnostics and database design while keeping
                                product and user needs front and center.
                            </p>
                        </SectionCard>

                        {/* Tech stack */}
                        <SectionCard id="stack" title="Tech stack">
                            <div className="grid gap-5 md:grid-cols-3">
                                <TechGroup
                                    icon={<Code2 className="h-4 w-4" />}
                                    label="Backend"
                                    items={[
                                        "Go (Golang)",
                                        "Python",
                                        "Node.js",
                                        "REST / JSON APIs",
                                        "gRPC",
                                    ]}
                                />
                                <TechGroup
                                    icon={<Database className="h-4 w-4" />}
                                    label="Data & Storage"
                                    items={[
                                        "PostgreSQL",
                                        "MySQL",
                                        "Redis",
                                        "Message queues",
                                        "S3-compatible storage",
                                    ]}
                                />
                                <TechGroup
                                    icon={<Cpu className="h-4 w-4" />}
                                    label="Infra & Tooling"
                                    items={[
                                        "Docker & containers",
                                        "Linux",
                                        "Reverse proxies",
                                        "CI/CD pipelines",
                                        "Monitoring & logging",
                                    ]}
                                />
                            </div>
                        </SectionCard>

                        {/* Experience */}
                        <SectionCard id="experience" title="Experience">
                            <ol className="relative border-l border-slate-700/70 dark:border-slate-300/70">
                                <TimelineItem
                                    period="2020 – Present"
                                    title="Senior / Lead Backend Developer"
                                    company="Rocinante Systems / Freelance"
                                    summary="Leading design and implementation of backend services and infrastructure for email processing and automation products."
                                    bullets={[
                                        "Designed scalable email ingestion and parsing pipelines.",
                                        "Owned core APIs, authentication, and integrations.",
                                        "Improved reliability and latency with better observability.",
                                    ]}
                                />
                                <TimelineItem
                                    period="2015 – 2020"
                                    title="Backend Engineer • CTO roles"
                                    company="BLUbike & other startups"
                                    summary="Hands-on technical leadership across architecture, implementation, and infrastructure for mobility and SaaS products."
                                    bullets={[
                                        "Developed microservices in Go and Python.",
                                        "Set technical direction and mentored engineers.",
                                        "Established CI/CD pipelines and containerized workloads.",
                                    ]}
                                />
                                <TimelineItem
                                    period="2010 – 2015"
                                    title="Software Engineer / Integration"
                                    company="Enterprise & product teams"
                                    summary="Built a strong foundation in testing, QA, and systems thinking while working on complex integrations."
                                    bullets={[
                                        "Designed automated regression testing frameworks.",
                                        "Debugged complex customer issues with support teams.",
                                    ]}
                                />
                            </ol>
                        </SectionCard>

                        {/* Projects */}
                        <SectionCard id="projects" title="Selected projects">
                            <div className="grid gap-5 md:grid-cols-2">
                                <ProjectCard
                                    title="InboxLogik"
                                    tag="SaaS · Email automation"
                                    description="Service that ingests raw email, converts it into structured JSON, and forwards it to user-defined webhooks."
                                    bullets={[
                                        "Designed backend architecture and message pipeline.",
                                        "Implemented custom parsing and flexible webhook delivery.",
                                        "Integrated with workflow tools like n8n.",
                                    ]}
                                />
                                <ProjectCard
                                    title="Let’s Proxy"
                                    tag="DevOps tooling"
                                    description="UI for configuring a reverse proxy via an intuitive dashboard."
                                    bullets={[
                                        "Built secure API layer to interact with proxy server.",
                                        "Implemented configuration validation and management.",
                                    ]}
                                />
                                <ProjectCard
                                    title="Monitoring & Ops Toolkit"
                                    tag="Developer experience"
                                    description="Internal tools for logs, metrics, and incident response."
                                    bullets={[
                                        "Reduced time-to-resolution by centralizing key signals.",
                                        "Standardised logging and tracing across services.",
                                    ]}
                                />
                                <ProjectCard
                                    title="Side Projects"
                                    tag="Personal"
                                    description="Smaller tools around AI-assisted workflows, developer utilities, and game HUD experiments."
                                    bullets={[
                                        "Rapid prototypes to test feasibility and UX quickly.",
                                    ]}
                                />
                            </div>
                        </SectionCard>

                        {/* Highlights */}
                        <SectionCard id="highlights" title="Highlights">
                            <ul className="list-disc space-y-2 pl-4 text-sm text-slate-300 dark:text-slate-700">
                                <li>
                                    Mensa member with strong abstract reasoning and
                                    problem-solving skills.
                                </li>
                                <li>
                                    Comfortable owning backend & infra lifecycle for small to
                                    mid-sized products.
                                </li>
                                <li>
                                    Experienced in fully remote collaboration across timezones.
                                </li>
                                <li>
                                    Enjoy writing clear technical documentation and mentoring
                                    developers.
                                </li>
                            </ul>
                        </SectionCard>

                        {/* Contact */}
                        <SectionCard id="contact" title="Let’s work together">
                            <p className="text-sm text-slate-300 dark:text-slate-700">
                                If you’re looking for someone to design, implement, and maintain
                                reliable backend systems — especially around email, automation,
                                and infrastructure — let’s chat.
                            </p>

                            <div className="mt-5 flex flex-wrap gap-3">
                                <a
                                    href="mailto:david.lednik@gmail.com"
                                    className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-4 py-2 text-xs font-semibold text-emerald-950 shadow-lg shadow-emerald-500/30 transition hover:bg-emerald-400"
                                >
                                    <Mail className="h-4 w-4" />
                                    <span>Email me</span>
                                    <ArrowRight className="h-4 w-4" />
                                </a>

                                <a
                                    href="https://calendly.com/david-rocinante/30min"
                                    className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-4 py-2 text-xs font-semibold text-emerald-950 shadow-lg shadow-emerald-500/30 transition hover:bg-emerald-400"
                                >
                                    <Calendar className="h-4 w-4" />
                                    <span>Book a meeting</span>
                                    <ArrowRight className="h-4 w-4" />
                                </a>

                                <a
                                    href="https://www.linkedin.com/in/david-lednik-1697013b" // update link
                                    className="inline-flex items-center gap-2 rounded-full border border-slate-600 bg-slate-900/80 px-4 py-2 text-xs font-semibold text-slate-100 hover:border-emerald-400 hover:text-emerald-200 dark:bg-white/80 dark:text-slate-800 dark:border-slate-300"
                                >
                                    <Linkedin className="h-4 w-4" />
                                    <span>View LinkedIn</span>
                                </a>
                            </div>
                        </SectionCard>

                        <motion.footer
                            className="pb-4 pt-2 text-xs text-slate-500 dark:text-slate-500"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.2 }}
                        >
                            <p>© {new Date().getFullYear()} David Lednik. All rights reserved.</p>
                        </motion.footer>
                    </main>
                </div>
            </div>
        </div>
    );
};

export default App;
